import * as Sentry from "@sentry/react";
import type { Metric } from "web-vitals";

/**
 * Reports Core Web Vitals to Sentry — both as a structured breadcrumb
 * (so they show up in any error trail later in the session) and as a
 * Sentry measurement on the active scope.
 *
 * Metrics tracked:
 *   - LCP  (Largest Contentful Paint) — perceived load time, target < 2.5s
 *   - INP  (Interaction to Next Paint) — interactivity, target < 200ms
 *   - CLS  (Cumulative Layout Shift)   — visual stability, target < 0.1
 *   - FCP  (First Contentful Paint)
 *   - TTFB (Time to First Byte)
 *
 * FID was retired in web-vitals v4 in favour of INP, which is the
 * metric that maps to the user-perceived "this page feels sluggish"
 * complaint. INP is also what the new Core Web Vitals threshold uses.
 *
 * Imported dynamically so the web-vitals package only weighs the
 * bundle for visitors who land on a real page (it polls
 * `PerformanceObserver`, no need to load it during bootstrap).
 */
export default function reportWebVitals(): void {
  if (typeof window === "undefined") return;
  void import("web-vitals").then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
    onCLS(sendMetric);
    onFCP(sendMetric);
    onINP(sendMetric);
    onLCP(sendMetric);
    onTTFB(sendMetric);
  });
}

function sendMetric(metric: Metric): void {
  Sentry.addBreadcrumb({
    category: "web-vital",
    message: metric.name,
    level: metric.rating === "poor" ? "warning" : "info",
    data: {
      value: Math.round(metric.value * 1000) / 1000,
      rating: metric.rating,
      id: metric.id,
    },
  });
  Sentry.setMeasurement(metric.name, metric.value, metric.name === "CLS" ? "" : "millisecond");
}
